import { useRef } from 'react';

import { Card } from '@components/common/Card';
import { Container } from '@components/common/Container';
import { Icon } from '@components/Icon';
import { LandingCardDeck } from '@components/features/landing/LandingCardDeck';

const QUOTE =
  '"Whatever you do, work at it with all your heart, as working for the Lord, not for human masters"';
const VERSE = 'Colossians 3:23';

const MISSION_BODY =
  'We are a Christian-led homeschool group who desire to honor God by providing an environment through education and socialization that aligns with His Word and to encourage and support homeschooling families as they grow in their relationships with the Lord and in their educational endeavors.';

const VISION_BODY =
  'As Bethlehem Homeschool Group in Knightdale, North Carolina, our vision is to make a God glorifying impact on the homeschool community in our area.';

const MISSION_CARDS = [
  {
    id: 'mission',
    title: 'Our Mission',
    body: MISSION_BODY,
    variant: 'mission' as const,
  },
  {
    id: 'vision',
    title: 'Our Vision',
    body: VISION_BODY,
    variant: 'vision' as const,
  },
] as const;

function missionCardClass(variant: 'mission' | 'vision', isSelected: boolean, isDimmed: boolean): string {
  return [
    'bhg-landing-mission__card',
    `bhg-landing-mission__card--${variant}`,
    'bhg-landing-card-deck__card',
    isSelected ? 'bhg-landing-card-deck__card--selected' : '',
    isDimmed ? 'bhg-landing-card-deck__card--dimmed' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function MissionSection() {
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <section
      id="mission"
      ref={sectionRef}
      className="bhg-landing-section bhg-landing-mission-section"
      aria-labelledby="mission-heading"
    >
      <Container size="lg">
        <div className="bhg-landing-mission">
          <div className="bhg-landing-mission__quote-block">
            <p className="bhg-landing-mission__overline">Our foundation</p>
            <blockquote className="bhg-landing-mission__quote">{QUOTE}</blockquote>
            <p className="bhg-landing-mission__verse">
              <span className="bhg-landing-mission__verse-inner">
                <Icon type="book" style="outlined" size="sm" colorVariant="blue" aria-hidden />
                {VERSE}
              </span>
            </p>
          </div>

          <LandingCardDeck
            items={MISSION_CARDS}
            rowClassName="bhg-landing-mission__cards"
            sectionRef={sectionRef}
            detailPanelId="mission-detail"
            inlineDetailMaxWidth={639}
            renderCard={(item, { isSelected, isDimmed, onSelect, onKeyDown }) => {
              const card = MISSION_CARDS.find((c) => c.id === item.id)!;
              return (
                <Card
                  elevation={2}
                  darkSurface
                  interactive
                  className={missionCardClass(card.variant, isSelected, isDimmed)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isSelected}
                  aria-controls="mission-detail"
                  aria-label={`${card.title}. ${isSelected ? 'Showing full text below' : 'Read more'}`}
                  onClick={onSelect}
                  onKeyDown={onKeyDown}
                >
                  <h3
                    id={card.variant === 'mission' ? 'mission-heading' : undefined}
                    className="bhg-landing-mission__card-title"
                  >
                    {card.title}
                  </h3>
                  <p className="bhg-landing-mission__card-text bhg-landing-mission__card-text--preview">
                    {card.body}
                  </p>
                  <p className="bhg-landing-mission__card-cta" aria-hidden>
                    {isSelected ? 'Showing below' : 'Read more'}
                  </p>
                </Card>
              );
            }}
            renderDetail={(item, { onClose }) => {
              const card = MISSION_CARDS.find((c) => c.id === item.id)!;
              return (
                <Card
                  elevation={3}
                  darkSurface
                  className={[
                    'bhg-landing-mission-detail',
                    `bhg-landing-mission-detail--${card.variant}`,
                  ].join(' ')}
                >
                  <div className="bhg-landing-mission-detail__header">
                    <h3 className="bhg-landing-mission__card-title">{card.title}</h3>
                    <button
                      type="button"
                      className="bhg-landing-card-deck__close"
                      onClick={onClose}
                      aria-label="Close"
                    >
                      Close
                    </button>
                  </div>
                  <p className="bhg-landing-mission__card-text">{card.body}</p>
                </Card>
              );
            }}
          />
        </div>
      </Container>
    </section>
  );
}
